import React, { createRef } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HelpIcon from '@mui/icons-material/Help';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ShareIcon from '@mui/icons-material/Share';
import { Badge, Box, Button, ListItemIcon, ListItemText, MenuItem, Tooltip } from '@mui/material';

import { createCookie, readCookie, TooltipIconButton } from '@paragon/client-tools';
import devTools from '@paragon/dev-tools';
import {
  canStartTrial,
  canUpgradeAccount,
  getClientUrls,
  getLoggedInUser,
  hasAssemblyPermission,
  hasTrussDesignPermission,
  isG2User,
  isLoggedInUserRoot,
  isWhiteLabeledParagonUser,
  shouldUserSeeReleaseNotes,
} from '@paragon/login-tools';
import { HeaderMobileMenu, ParagonHeader } from '@paragon/material';
import { WalkthroughBlurbAnchor } from '@paragon/tour-guide';

import g2DesignBadgeUrl from '../../../resources/G2DesignBadge.png';
import { RootState } from '../../store';
import { logOutLinkUser } from '../../store/actions/appActions';
import {
  showCreateEstimateDialog,
  showLogInDialog,
  showShareDialog,
  showSignUpDialog,
  showUpgradeAccountDialog,
  toggleHotkeyDialog,
} from '../../store/actions/popupActions';
import { getHeaderText } from '../../store/selectors/getHeaderText';
import { isLinkUser } from '../../store/selectors/isLinkUser';
import { isParagonRepairJobOpen } from '../../store/selectors/jobContextSelectors';
import { isViewingOtherOrganizationsComponent } from '../../store/selectors/viewOnlyModeSelectors';
import { AssistantHeaderButton, AssistantMenuItem } from '../assistant/AssistantRoot';
import { withInMobileMode } from '../hooks';
import HeaderFileUploader from '../misc/HeaderFileUploader';
import { SimpleModeToggle } from '../toolbars/buttons/SimpleModeToggle';
import { ContextToolbar } from './ContextToolbar';
import { CopyToMyOrganization } from './CopyToMyOrganization';
import { DesignClientUserDropdown } from './DesignClientUserDropdown';
import { CURRENT_VERSION } from './OpenUserSettingsMenuItem';
import { ReleaseNotesPopover } from './ReleaseNotesPopover';

const HAS_RELEASE_NOTES = true;

interface State {
  readonly releaseNotesPopover: HTMLSpanElement | null;
}

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type OwnProps = { onToggleDocsPanel: () => void };
type Props = StateProps & DispatchProps & OwnProps & { inMobileMode: boolean };

class Header extends React.Component<Props, State> {
  state: State = {
    releaseNotesPopover: null,
  };

  logoRef = createRef<HTMLSpanElement>();

  showingWelcomeDialog = Boolean(new URLSearchParams(window.location.search).get('welcome'));

  shouldShowReleaseNotes() {
    return HAS_RELEASE_NOTES && +readCookie('releaseNotesSeen')! < CURRENT_VERSION && !this.showingWelcomeDialog;
  }

  componentDidMount() {
    if (this.shouldShowReleaseNotes()) {
      this.setState({ releaseNotesPopover: this.logoRef.current! });
    }
  }

  dismiss = () => {
    this.setState({ releaseNotesPopover: null });
    createCookie('releaseNotesSeen', CURRENT_VERSION.toString(), { days: 365 });
  };

  renderHeaderTextWithBadge() {
    let text = (
      <Box component="span" sx={{ display: { xs: 'none', lg: 'initial' } }}>
        {this.props.headerText}
      </Box>
    );

    if (this.props.canSeeReleaseNotes) {
      text = (
        <span
          ref={this.logoRef}
          onClick={(event) => {
            if (HAS_RELEASE_NOTES) {
              this.setState({ releaseNotesPopover: event.target as HTMLSpanElement });
            }
          }}
        >
          {text}
        </span>
      );

      if (this.shouldShowReleaseNotes()) {
        text = (
          <Badge color="success" variant="dot">
            {text}
          </Badge>
        );
      }
    }

    const releaseNotesUrl = `https://docs.paragontruss.com/release-notes/current`;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          {text}
          <SimpleModeToggle sx={{ marginLeft: { xs: -1, md: 0, lg: 2 } }} />
        </Box>
        <ReleaseNotesPopover url={releaseNotesUrl} anchorEl={this.state.releaseNotesPopover} onClose={this.dismiss} />
      </Box>
    );
  }

  render() {
    const {
      openContext,
      isInParagonRepairJob,
      isRoot,
      loggedInUser,
      isLinkUser,
      canAddToStation,
      canUpgradeAccount,
      canStartTrial,
      servicesUrl,
      showUpgradeAccountDialog,
      showSignUpDialog,
    } = this.props;

    const uploadingIsPossible =
      this.props.hasTrussDesignPermission &&
      !this.props.isViewingOtherOrganizationsComponent &&
      (openContext === 'job' ? isInParagonRepairJob : true);

    const userIsReal = loggedInUser && !isLinkUser;

    function onClickSignUp() {
      if (isWhiteLabeledParagonUser()) {
        window.open(`${servicesUrl}signup`, '_blank');
      } else {
        showSignUpDialog();
      }
    }

    return (
      <ParagonHeader
        clientLogoUrl={this.props.useG2Theme ? g2DesignBadgeUrl : undefined}
        hideClientLogo={this.props.inMobileMode}
        clientName="paragon-design"
        showTitleAtAllSizes
        text={this.renderHeaderTextWithBadge()}
        showUpgradeAccountButton={canUpgradeAccount}
        upgradeAccountButtonText={canStartTrial ? 'Start Free Trial' : 'Upgrade'}
        upgradeAccountAction={() => void showUpgradeAccountDialog()}
        renderDevToolsButton={false}
        toolbarSx={{
          display: 'flex',
          '& > *': { flex: '1 1 0px' },
        }}
      >
        <WalkthroughBlurbAnchor id="context-toolbar" blurbPlacement="bottom">
          <ContextToolbar />
        </WalkthroughBlurbAnchor>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'flex-end',
            '& button': {
              whiteSpace: 'nowrap',
            },
          }}
        >
          <Box sx={{ display: { xs: 'none', xl: 'contents' } }}>
            {/* when working with these buttons, please mirror your changes in the mobile menu below if appropriate */}

            {openContext === 'project' || openContext === 'shared-component-set' ? (
              <Tooltip title="Get shareable link, TDDs, or request seals">
                <Button variant="outlined" startIcon={<ShareIcon />} onClick={this.props.showShareDialog}>
                  Share
                </Button>
              </Tooltip>
            ) : null}

            <CopyToMyOrganization displayType="button" />

            {openContext === 'project' || openContext === 'shared-component-set' ? (
              <Button variant="outlined" startIcon={<RequestQuoteIcon />} onClick={this.props.showCreateEstimateDialog}>
                Create Estimate
              </Button>
            ) : null}
            {uploadingIsPossible && <HeaderFileUploader variant="upload" client="desktop" />}
            {uploadingIsPossible && isRoot && <HeaderFileUploader variant="uploadAndAnalyze" client="desktop" />}
            {this.props.canAddToStation && <HeaderFileUploader variant="addToStation" client="desktop" />}

            {!userIsReal && (
              <Button
                variant="outlined"
                onClick={() => (isLinkUser ? this.props.logOutLinkUser() : this.props.showLogInDialog())}
              >
                Log In
              </Button>
            )}
          </Box>

          {!userIsReal && (
            <>
              <Button
                sx={{ display: { xs: 'flex', md: 'none' } }}
                size="small"
                variant="contained"
                color="secondary"
                onClick={onClickSignUp}
              >
                Sign Up
              </Button>
              <Button
                sx={{ display: { xs: 'none', md: 'flex' } }}
                variant="contained"
                color="secondary"
                onClick={onClickSignUp}
              >
                Sign Up
              </Button>
            </>
          )}

          {userIsReal && (
            //FIXME DAVID - if a user is viewing a shared component set and not logged in, this breaks
            // <WalkthroughBlurbAnchor id="user-menu" blurbPlacement="bottom">
            <DesignClientUserDropdown />
            // </WalkthroughBlurbAnchor>
          )}

          <AssistantHeaderButton sx={{ display: { xs: 'none', xl: 'flex' } }} />

          <TooltipIconButton
            title="Ask a Question"
            sx={{ display: { xs: 'none', xl: 'flex' } }}
            onClick={this.props.onToggleDocsPanel}
          >
            <HelpIcon />
          </TooltipIconButton>

          <TooltipIconButton
            title="Hotkeys"
            sx={{ display: { xs: 'none', xl: 'flex' } }}
            onClick={() => this.props.toggleHotkeyDialog()}
          >
            <KeyboardIcon />
          </TooltipIconButton>

          {devTools.renderDevToolsButton({ sx: { display: { xs: 'none', xl: 'flex' } } })}

          <HeaderMobileMenu sx={{ display: { xs: 'flex', xl: 'none' } }}>
            {/* when working with this menu, please mirror your changes to the desktop buttons above if appropriate */}

            {!userIsReal && (
              <MenuItem onClick={() => (isLinkUser ? this.props.logOutLinkUser() : this.props.showLogInDialog())}>
                <ListItemIcon>
                  <AccountCircleIcon />
                </ListItemIcon>
                <ListItemText>Log In</ListItemText>
              </MenuItem>
            )}

            {(openContext === 'project' || openContext === 'shared-component-set') && (
              <MenuItem onClick={this.props.showShareDialog}>
                <ListItemIcon>
                  <ShareIcon />
                </ListItemIcon>
                <ListItemText>Share</ListItemText>
              </MenuItem>
            )}

            <CopyToMyOrganization displayType="menuItem" />

            {uploadingIsPossible && <HeaderFileUploader variant="upload" client="mobile" />}
            {uploadingIsPossible && isRoot && <HeaderFileUploader variant="uploadAndAnalyze" client="mobile" />}
            {canAddToStation && <HeaderFileUploader variant="addToStation" client="mobile" />}

            <AssistantMenuItem />

            <MenuItem onClick={this.props.onToggleDocsPanel}>
              <ListItemIcon>
                <HelpIcon />
              </ListItemIcon>
              <ListItemText>Ask a Question</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => this.props.toggleHotkeyDialog()}>
              <ListItemIcon>
                <KeyboardIcon />
              </ListItemIcon>
              <ListItemText>Hotkeys</ListItemText>
            </MenuItem>

            {devTools.renderDevToolsButton({ sx: { alignSelf: 'center' }, client: 'mobile' })}
          </HeaderMobileMenu>
        </Box>
      </ParagonHeader>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  headerText: getHeaderText(state),
  useG2Theme: isG2User(state),
  isRoot: isLoggedInUserRoot(state),
  hasTrussDesignPermission: hasTrussDesignPermission(state),
  isInParagonRepairJob: isParagonRepairJobOpen(state),
  canAddToStation: hasAssemblyPermission(state) && state.application.openContext?.type === 'station',
  canSeeReleaseNotes: shouldUserSeeReleaseNotes(state),
  isLinkUser: isLinkUser(state),
  loggedInUser: getLoggedInUser(state),
  isViewingOtherOrganizationsComponent: isViewingOtherOrganizationsComponent(state),
  openContext: state.application.openContext?.type,
  canUpgradeAccount: canUpgradeAccount(state),
  canStartTrial: canStartTrial(state),
  servicesUrl: getClientUrls(state).servicesUrl,
});

const actionCreators = {
  showLogInDialog,
  showSignUpDialog,
  logOutLinkUser,
  showShareDialog,
  showCreateEstimateDialog,
  showUpgradeAccountDialog,
  toggleHotkeyDialog,
};

export default connect(
  mapStateToProps,
  actionCreators,
)(withInMobileMode<StateProps & DispatchProps & OwnProps>(Header));
